import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import { updateAnalyticsIdentity } from "../../shared/utils/analyticsIdentity";
import { fireQuoteShownConversion } from "../../utils/adsConversionUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

const QuoteSubmission = ({
  transactionType,
  personalDetails,
  sellingDetails,
  buyingDetails,
  defaultChannel,
  defaultReferrer,
  referral,
  googleAnalyticsId,
  campaign,
  onFlowComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref guard prevents StrictMode double-mount from firing a second request
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    if (personalDetails == null) {
      console.error("Quote submission blocked: personalDetails is null");
      captureSubmissionError("Quote", 0, "personalDetails was null at submission time");
      setError("Unable to generate your quote");
      setLoading(false);
      return;
    }

    const handleError = (err) => {
      console.error("Quote submission failed:", err);
      setError("Unable to generate your quote");
      setLoading(false);
    };

    const channel = referral?.referralSource || defaultChannel;
    const referrer = referral?.referralSource ? referral.referralCode : defaultReferrer;

    fetch("/api/quote", {
      method: "PUT",
      body: JSON.stringify({
        transactionType,
        channel,
        referrer,
        personalDetails,
        sellingDetails: transactionType === "Selling" || transactionType === "BuyingAndSelling" ? sellingDetails : null,
        buyingDetails: transactionType === "Buying" || transactionType === "BuyingAndSelling" ? buyingDetails : null,
        remortgagingDetails: null,
        referralCode: referral ? referral.referralCode : null,
        googleAnalyticsId,
        campaign: campaign ?? null,
        quotePage: true,
      }),
    })
      .then(async (resp) => {
        if (resp.ok) {
          const quoteData = await resp.json();

          try {
            if (personalDetails?.emailAddress) {
              updateAnalyticsIdentity(personalDetails.emailAddress.trim(), "lead");
            }
          } catch (analyticsErr) {
            console.error("Analytics identity update failed:", analyticsErr);
          }

          // The primary Google Ads conversion: a lead exists and the quote is
          // about to be shown. Fired here rather than on mount so a failed
          // submission never counts, and so the value can come from the
          // response.
          fireQuoteShownConversion(quoteData);

          setLoading(false);
          onFlowComplete({
            quoteDetails: quoteData,
          });
        } else {
          const body = await resp.text().catch(() => "");
          console.error("Quote submission failed:", resp.status, body);
          captureSubmissionError("Quote", resp.status, body);
          setError("Unable to generate your quote");
          setLoading(false);
        }
      }, handleError)
      // Catches errors thrown inside the .then() handlers (e.g. resp.json() SyntaxError)
      .catch(handleError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- submit once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard title="Working out your price" text="This won’t take long." />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default QuoteSubmission;
